'use client';
import React from 'react';
import { useGed } from '@/lib/store';
import { ORG_TIERS, ROLES_ORG, tierOfUser } from '@/lib/meta';
import { pastilleDe } from '@/lib/couleurs-personne';
import { I, card, useEscape } from '@/components/ui';
import { Bouton } from '@/shared/Bouton';
import type { GedUser } from '@/lib/types';
import { initials } from './commun';
import { nomDe } from '@/lib/nom';

/**
 * RÉSUMÉ D'UNE PERSONNE — ce que l'organigramme montre, et rien de plus
 * (RQ, 06/10/2026).
 *
 * ── La décision ─────────────────────────────────────────────────────────────
 *
 * « Le dossier RH contient tout tout tout. Quand je clique sur l'organigramme,
 * je veux voir un résumé non modifiable. Pour le modifier, il faut aller sur le
 * dossier RH. C'est à partir du dossier RH qu'on gère tout ce qui est relié à
 * la personne. L'organigramme donne juste une sorte de résumé non modifiable. »
 *
 * Jusqu'au 06/10, un clic sur une carte ouvrait la fiche COMPLÈTE et éditable —
 * la même qu'au dossier RH. Deux conséquences, toutes deux constatées :
 *
 *  · on modifiait le dossier depuis un écran dont l'objet est de montrer une
 *    STRUCTURE, et les deux gestes se gênaient (on ne consulte pas l'arbre avec
 *    une saisie ouverte par-dessus) ;
 *  · il y avait deux portes d'entrée vers la même écriture, donc deux endroits
 *    à tenir à jour, et un seul des deux était pensé pour ça.
 *
 * Ce panneau est donc en LECTURE SEULE, sans exception : aucun champ, aucun
 * bouton d'écriture, pas même la couleur. Le seul geste qu'il propose est
 * d'aller là où l'écriture vit.
 *
 * ── Ce qu'il ne montre pas, et pourquoi ─────────────────────────────────────
 *
 * ⚠️ L'organigramme est OUVERT À TOUT LE PERSONNEL. Ce résumé ne porte donc
 * que ce que l'organigramme publie déjà : identité, position, responsabilités.
 * Ni badge, ni matricule, ni RIB, ni téléphone, ni dates d'entrée et de sortie —
 * ces rubriques sont réservées au gestionnaire RH, et les afficher ici les
 * aurait servies à qui consulte l'arbre. Le cloisonnement reste celui du
 * serveur : `rh-personnel` n'est tout simplement pas servi à ces comptes, et
 * l'état du compte de connexion n'apparaît donc qu'à qui le reçoit.
 *
 * ⚠️ NI VOILE, NI FERMETURE AU CLIC À CÔTÉ. Le panneau est posé à droite et
 * l'arbre reste visible ET cliquable derrière : c'est tout l'intérêt par
 * rapport à la fenêtre qu'il remplace — on passe d'une personne à l'autre en
 * cliquant les cartes, le panneau suit. Un voile aurait rendu ce geste
 * impossible. Il n'est donc pas `aria-modal` : rien n'est piégé dedans.
 */

const SECTION: React.CSSProperties = {
  fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.07em', color: 'var(--text-muted)', margin: '14px 0 7px',
};

const vide = <span style={{ color: 'var(--text-faint)' }}>—</span>;

function Ligne({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '92px 1fr', gap: 10, padding: '4px 0', alignItems: 'baseline' }}>
      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontSize: 12.5, color: 'var(--text-strong)', lineHeight: 1.5 }}>{children}</span>
    </div>
  );
}

/** Libellé du statut de DOSSIER — repris de la fiche, même vocabulaire. */
const STATUT_DOSSIER: Record<string, { label: string; bg: string; fg: string }> = {
  actif: { label: 'En service', bg: 'var(--green-50)', fg: 'var(--green-600)' },
  suspendu: { label: 'Suspendu', bg: 'var(--amber-50)', fg: 'var(--amber-600)' },
  sorti: { label: 'Sorti des effectifs', bg: 'var(--gray-50)', fg: 'var(--text-muted)' },
};

const ETAT_COMPTE: Record<string, { label: string; fg: string }> = {
  actif: { label: 'Compte actif', fg: 'var(--green-600)' },
  invite: { label: 'Invitation en attente', fg: 'var(--amber-600)' },
  expiree: { label: 'Invitation expirée', fg: 'var(--red-600)' },
  suspendu: { label: 'Compte suspendu', fg: 'var(--amber-600)' },
  archive: { label: 'Compte archivé', fg: 'var(--text-muted)' },
};

export function ResumePersonne({ orgId, onFermer, onTransfert }: {
  orgId: string;
  onFermer: () => void;
  /** LE TRANSFERT DE RESPONSABILITÉS reste un geste de l'organigramme : son
   *  panneau y vit (`TransfertPanel`), il se remplit au fil des choix et
   *  l'écran a la place de l'afficher. Il n'avait AUCUNE autre porte que la
   *  fiche — la retirer d'ici l'aurait rendu inatteignable depuis l'arbre, et
   *  « Dossiers RH » renvoie justement à l'arbre pour le faire. Absent : le
   *  bouton n'apparaît pas (le résumé s'ouvre aussi hors organigramme). */
  onTransfert?: (orgId: string) => void;
}) {
  const { settings, session, rhAcces, rhPersonnel, naviguerVers } = useGed();

  useEscape(true, onFermer);

  const users: GedUser[] = settings?.gedUsers || [];
  const rh = rhPersonnel.find((x) => x.orgId === orgId);
  const uOrg = users.find((x) => x.id === orgId);
  /* Même repli que la fiche : `gedUsers` est la VUE, `rh-personnel` la SOURCE.
     Une personne hors organigramme n'a que la seconde. */
  const u: GedUser | undefined = uOrg || (rh ? {
    id: rh.orgId, nom: nomDe(rh), initiales: rh.initiales || '', fonction: rh.fonction || '',
    site: rh.site || '', roles: rh.roles || [],
    orgTier: rh.orgTier || undefined,
    responsable: rh.responsableOrgId || null,
    lien: rh.lienResponsable === 'fonctionnel' ? 'fonctionnel' : 'hierarchique',
    fonctionIds: rh.fonctionIds || [],
    statut: rh.statut,
  } : undefined);
  if (!u) return null;

  const nomAff = rh ? nomDe(rh) : (u.nom || '');
  const initialesAff = (rh?.initiales ?? u.initiales) || initials(nomAff);
  const pastille = pastilleDe(orgId, rh?.couleur || '');

  const responsable = users.find((x) => x.id === u.responsable);
  const encadres = users.filter((x) => x.responsable === u.id);
  const fonctionsTenues = (settings?.orgFonctions || [])
    .filter((f) => (u.fonctionIds || []).includes(f.id));
  const processus = settings?.processus || [];
  const pilote = processus.filter((p) => p.pilote === orgId);
  const supplee = processus.filter((p) => p.suppleant === orgId);
  const rolesOrg = rh?.rolesOrg || [];

  const compte = rh?.compteEtat || null;
  const etatCompte = compte ? ETAT_COMPTE[compte.etat] : null;
  const statut = String(u.statut || rh?.statut || 'actif');
  const badge = STATUT_DOSSIER[statut] || STATUT_DOSSIER.actif;

  /* LE BOUTON N'EST PROPOSÉ QU'À QUI PEUT OUVRIR LE DOSSIER : même condition
     que l'entrée « Dossiers RH » de la barre (rôle `rh` ET portée large). Un
     bouton qui mène à « rubrique réservée » est pire que pas de bouton — il
     fait croire à une panne de droits ponctuelle. */
  const peutOuvrirDossier = !!rhAcces?.acces && session.sections?.dossierRh?.portee === 'tout';
  const isRQ = session.roles.includes('rq');

  return (
    <aside role="dialog" aria-label={`Résumé de ${nomAff}`}
      style={{
        position: 'fixed', top: 0, bottom: 0, insetInlineEnd: 0, width: 'min(360px, 100vw)',
        zIndex: 90, background: 'var(--surface-card)', borderInlineStart: '1px solid var(--border-default)',
        boxShadow: '-12px 0 32px rgba(11,20,38,0.12)', overflowY: 'auto', padding: '18px 18px 28px',
      }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        {/* ⚠️ LE ROND N'EST PAS CLIQUABLE ICI. Sur la fiche, il ouvre la palette
            de couleurs ; ici il ne fait rien, et c'est exprès — ce panneau
            n'écrit pas. Pas de `<button>` autour : un rond qui a l'air
            cliquable et ne l'est pas se lit comme un défaut. */}
        <span aria-hidden style={{
          width: 44, height: 44, flex: 'none', borderRadius: '50%',
          background: pastille.background, color: pastille.color,
          border: `2px solid ${pastille.borderColor}`,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'var(--font-mono)', fontSize: 14, fontWeight: 700,
        }}>{initialesAff}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-strong)' }}>{nomAff}</h2>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            {rh?.intitule || u.fonction || '—'}
          </div>
        </div>
        <Bouton taille="sm" onClick={onFermer} aria-label={`Fermer le résumé de ${nomAff}`}>
          <I n="x" s={13} />
        </Bouton>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
        <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '3px 9px', background: badge.bg, color: badge.fg }}>
          {badge.label}
        </span>
        {/* Rien n'est affirmé sur le compte quand la fiche RH n'est pas servie :
            son absence ne veut pas dire « pas de compte ». */}
        {rh && (etatCompte
          ? <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '3px 9px', background: 'var(--surface-sunken)', color: etatCompte.fg }}>{etatCompte.label}</span>
          : <span style={{ fontSize: 10.5, borderRadius: 'var(--radius-pill)', padding: '3px 9px', background: 'var(--gray-50)', color: 'var(--text-muted)' }}>Sans compte de connexion</span>)}
      </div>

      {!!rh?.numero && (
        <div style={{ marginTop: 10, fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 700,
          color: 'var(--text-strong)', background: 'var(--surface-sunken)', border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-sm)', padding: '3px 9px', display: 'inline-block', userSelect: 'all' }}>
          {rh.numero}
        </div>
      )}

      <h3 style={SECTION}>Position</h3>
      <div style={{ ...card, padding: '8px 12px' }}>
        <Ligne label="Niveau">{ORG_TIERS.find((t) => t.key === tierOfUser(u))?.label || vide}</Ligne>
        <Ligne label="Site">{u.site || vide}</Ligne>
        <Ligne label="Rattaché(e) à">
          {responsable
            ? <>{responsable.nom}{u.lien === 'fonctionnel' && <span style={{ color: 'var(--text-muted)' }}> · fonctionnel</span>}</>
            : <span style={{ color: 'var(--text-muted)' }}>Racine de l’organigramme</span>}
        </Ligne>
        <Ligne label="Encadre">
          {encadres.length ? `${encadres.length} personne(s)` : vide}
        </Ligne>
        <Ligne label="Postes tenus">
          {fonctionsTenues.length ? fonctionsTenues.map((f) => f.label).join(' · ') : vide}
        </Ligne>
      </div>

      <h3 style={SECTION}>Responsabilités</h3>
      <div style={{ ...card, padding: '8px 12px' }}>
        <Ligne label="Rôles">
          {rolesOrg.length ? rolesOrg.map((r) => ROLES_ORG[r] || r).join(' · ') : vide}
        </Ligne>
        <Ligne label="Pilote de">{pilote.length ? pilote.map((p) => p.name).join(' · ') : vide}</Ligne>
        <Ligne label="Suppléant de">{supplee.length ? supplee.map((p) => p.name).join(' · ') : vide}</Ligne>
      </div>

      {/* ── LA PHRASE QUI ÉVITE DE CHERCHER ────────────────────────────────────
          Un panneau sans aucun champ de saisie se lit volontiers comme « je n'ai
          pas le droit » ou « ça n'a pas chargé ». On dit donc que la lecture
          seule est une DÉCISION, et où l'écriture se trouve. */}
      <p style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.55, margin: '14px 0 0' }}>
        Ce résumé ne se modifie pas : tout ce qui concerne la personne — identité,
        contrat, compétences, temps, pièces — se gère dans son <strong>dossier RH</strong>.
      </p>
      {peutOuvrirDossier ? (
        <Bouton variante="primaire" taille="md" style={{ width: '100%', marginTop: 10 }}
          onClick={() => { onFermer(); naviguerVers('dossiers', { cle: 'selectedPersonne', valeur: orgId }); }}>
          <I n="suitcase" s={13} />Ouvrir le dossier RH
        </Bouton>
      ) : (
        <p style={{ fontSize: 12, color: 'var(--text-faint)', lineHeight: 1.55, margin: '8px 0 0' }}>
          Le dossier RH est réservé au gestionnaire RH. Le vôtre est dans
          « Mon dossier RH », depuis le rond de votre profil.
        </p>
      )}

      {/* ⚠️ LA SEULE ACTION DE CE PANNEAU, et elle n'écrit rien par elle-même :
          elle ouvre le panneau de transfert, qui vit dans l'écran. Réservée au
          RQ, et jamais sur sa propre fiche — le serveur refuse qu'on se
          transfère ses propres responsabilités. */}
      {onTransfert && isRQ && (
        <Bouton taille="sm" style={{ width: '100%', marginTop: 8 }}
          disabled={orgId === session.key}
          title={orgId === session.key
            ? 'On ne transfère pas ses propres responsabilités depuis sa fiche.'
            : 'Confier ses pilotages, suppléances et encadrements à quelqu’un d’autre.'}
          onClick={() => { onFermer(); onTransfert(orgId); }}>
          <I n="send" s={12} />Transférer ses responsabilités
        </Bouton>
      )}
    </aside>
  );
}
